import {
  calloutBindingProps,
  type CalloutBinding,
  type CalloutShape,
} from "sketch/shapes/callout";
import {
  BindingUtil,
  type BindingOnCreateOptions,
  type BindingOnShapeChangeOptions,
} from "tldraw";

import { boundAnchorPage } from "@/sketch/bound-point";

/**
 * Keeps a callout's leader landing on the element it annotates: when the
 * target moves (or the binding is created), the resolved anchor point is
 * written into the callout's props. That bumps the callout record, so its
 * geometry recomputes, remote peers see the move, and the agent's projection
 * reads a fresh point even without an editor. Deleting the target leaves the
 * last resolved point behind as a free leader, the same graceful degrade a
 * dim makes.
 */
export class CalloutBindingUtil extends BindingUtil<CalloutBinding> {
  static override type = "callout" as const;
  static override props = calloutBindingProps;

  override getDefaultProps(): Partial<CalloutBinding["props"]> {
    return { anchor: { x: 0.5, y: 0.5 } };
  }

  override onAfterCreate({ binding }: BindingOnCreateOptions<CalloutBinding>) {
    this.refresh(binding);
  }

  override onAfterChangeToShape({
    binding,
  }: BindingOnShapeChangeOptions<CalloutBinding>) {
    this.refresh(binding);
  }

  private refresh(binding: CalloutBinding) {
    const callout = this.editor.getShape<CalloutShape>(binding.fromId);
    const point = boundAnchorPage(this.editor, binding);
    if (!callout || !point) return;
    const current = callout.props.target;
    if (
      Math.abs(current.x - (point.x - callout.x)) < 1e-6 &&
      Math.abs(current.y - (point.y - callout.y)) < 1e-6
    ) {
      return;
    }
    this.editor.updateShape<CalloutShape>({
      id: callout.id,
      type: "callout",
      props: {
        target: { x: point.x - callout.x, y: point.y - callout.y },
      },
    });
  }
}
